// ⬡B:ccwa_core.use_mood_session:CODE:turnkey_air_voice_session:20260613⬡
// useMoodSession — the one shared session hook for every mood (Front-End Model item 15).
// Encapsulates the AIR WebSocket (live captions) + HTTP fallback, message state, send,
// the VARA voice loop, the Luma turn-explainer feed, and the agent side-chat. A mood
// becomes:  const s = useMoodSession({ channel, hamHint });  then renders the shared
// shell with s. Channel + hamHint are the only inputs; widgets stay in the mood.
// Lifted verbatim from the cara/ccwa route wiring so behaviour is identical.

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { resolveAnewApiBase, resolveAnewWebSocketBase } from '@aba/api-core';
import { useVaraVoice } from './useVaraVoice.js';
// ⬡B:DC499D0C:FIX:turn_could_wait_on_a_channel_forever:20260801⬡
// Neighbour of the Jobs-tab loading fix, found while tracing what that tab
// waits on: once a message actually goes out (WS open, or the HTTP fallback
// below), nothing ever bounded the wait for a reply. A WS `complete`/`error`
// frame that never arrives left isTyping stuck true with no cap; the HTTP
// fetch carried no timeout, so a backend that accepted the connection and
// never answered would hang the same promise indefinitely. Same founder's
// law as the sign-in gate: an async state ends in the real thing or an
// honest fallback, never an endless impression that it is still coming.
// The actual timing math lives in ./turnTimeout.js, split out on purpose: it
// is pure and has zero imports, unlike this file (which reaches
// @aba/voice-core, a .jsx package), so a lock test can import and assert on
// the real function directly under the bare `node --test` runner this repo
// uses, rather than only being provable by reading source text.
import { computeTurnTimeoutMs } from './turnTimeout.js';

const AIR_WS_URL   = `${resolveAnewWebSocketBase()}/api/air/ws`;
const AIR_ENDPOINT = `${resolveAnewApiBase()}/api/air/process`;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS  = 16000;
const MAX_RETRIES       = 5;
const HTTP_FETCH_TIMEOUT_MS   = 20000;
const TIMEOUT_REPLY_TEXT      = 'Something went wrong reaching A’NU.';

export function toAuraState(voiceState, isMuted) {
  if (isMuted)     return 'muted';
  if (!voiceState) return 'idle';
  switch (voiceState) {
    case 'listening':  return 'listening';
    case 'speaking':   return 'speaking';
    case 'thinking':   return 'thinking';
    case 'connecting': return 'thinking';
    case 'idle':       return 'idle';
    default:           return 'idle';
  }
}

export function humanDuration(ms) {
  if (ms == null) return '';
  if (ms < 1000)  return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function useAirSocket({ onNarration, onComplete, onError, onStatusChange, channel, hamHint }) {
  const wsRef         = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimer    = useRef(null);
  const mountedRef    = useRef(true);
  const [wsReady, setWsReady] = useState(false);

  const cbNarration    = useRef(onNarration);
  const cbComplete     = useRef(onComplete);
  const cbError        = useRef(onError);
  const cbStatusChange = useRef(onStatusChange);
  const channelRef     = useRef(channel);
  const hamHintRef     = useRef(hamHint);
  useEffect(() => { cbNarration.current    = onNarration;    }, [onNarration]);
  useEffect(() => { cbComplete.current     = onComplete;     }, [onComplete]);
  useEffect(() => { cbError.current        = onError;        }, [onError]);
  useEffect(() => { cbStatusChange.current = onStatusChange; }, [onStatusChange]);
  useEffect(() => { channelRef.current     = channel;        }, [channel]);
  useEffect(() => { hamHintRef.current     = hamHint;        }, [hamHint]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} wsRef.current = null; }
    let ws;
    try { ws = new WebSocket(AIR_WS_URL); } catch (err) { cbError.current?.('ws_init_failed'); return; }
    wsRef.current = ws;
    ws.onopen = () => {
      if (!mountedRef.current) { ws.close(); return; }
      retryCountRef.current = 0; setWsReady(true); cbStatusChange.current?.('open');
    };
    ws.onmessage = (evt) => {
      if (!mountedRef.current) return;
      let frame; try { frame = JSON.parse(evt.data); } catch (_) { return; }
      if (frame.type === "event" && frame.data?.type === "chatter_narration") { cbNarration.current?.(frame.data?.text ?? ""); }
      else if (frame.type === "event") { cbNarration.current?.(null); }
      else if (frame.type === "complete") { cbComplete.current?.(frame.data ?? frame); }
      else if (frame.type === "error") { cbError.current?.(frame.message ?? "ws_server_error"); }
    };
    ws.onerror = () => {};
    ws.onclose = () => {
      if (!mountedRef.current) return;
      setWsReady(false); cbStatusChange.current?.('closed');
      if (retryCountRef.current >= MAX_RETRIES) { cbError.current?.('ws_max_retries'); return; }
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** retryCountRef.current, RECONNECT_MAX_MS);
      retryCountRef.current += 1;
      retryTimer.current = setTimeout(connect, delay);
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true; connect();
    return () => { mountedRef.current = false; clearTimeout(retryTimer.current); try { wsRef.current?.close(); } catch (_) {} };
  }, [connect]);

  const sendMessage = useCallback((text) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ message: text, channel: channelRef.current, hamHint: hamHintRef.current }));
      return true;
    }
    return false;
  }, []);

  return { wsReady, sendMessage };
}

export function useMoodSession({ channel = 'cara', hamHint = null } = {}) {
  const [messages, setMessages]       = useState([]);
  const [isTyping, setIsTyping]       = useState(false);
  const [lumaEntries, setLumaEntries] = useState([]);
  const [threads, setThreads]         = useState({});
  const [working, setWorking]         = useState({});
  const turnCounterRef        = useRef(0);
  const wsTurnHasNarrationRef = useRef(false);
  const t0Ref                 = useRef(0);
  const turnActiveRef         = useRef(false);
  const turnTimeoutRef        = useRef(null);

  const pushLuma = useCallback((replyText, routeField, durationMs) => {
    turnCounterRef.current += 1;
    const turnId = turnCounterRef.current;
    const layman = replyText.split(/[.!?]/)[0].trim() || replyText.slice(0);
    const routeName = routeField
      ? (typeof routeField === 'object' ? (routeField.name ?? JSON.stringify(routeField)) : String(routeField))
      : null;
    const techParts = [humanDuration(durationMs)];
    if (routeName) techParts.push(`route: ${routeName}`);
    setLumaEntries((prev) => [...prev, { turnId, at: new Date().toISOString(), layman, technical: techParts.join(' \u00b7 ') }]);
  }, []);

  // endTurn: the one place that stops the watchdog and marks the turn
  // resolved, whichever path resolved it (WS complete, HTTP reply, or the
  // watchdog itself giving up honestly). Idempotent by design -- whichever
  // caller reaches it first wins, the rest are no-ops via turnActiveRef.
  const endTurn = useCallback(() => {
    turnActiveRef.current = false;
    if (turnTimeoutRef.current) { clearTimeout(turnTimeoutRef.current); turnTimeoutRef.current = null; }
  }, []);

  const armTurnTimeout = useCallback(() => {
    if (turnTimeoutRef.current) clearTimeout(turnTimeoutRef.current);
    const elapsed   = Date.now() - t0Ref.current;
    const remaining = computeTurnTimeoutMs(elapsed);
    turnTimeoutRef.current = setTimeout(() => {
      if (!turnActiveRef.current) return;
      endTurn();
      setMessages((prev) => [
        ...prev.filter((m) => !m._live),
        { role: 'assistant', text: TIMEOUT_REPLY_TEXT, at: new Date().toISOString() }
      ]);
      setIsTyping(false);
      wsTurnHasNarrationRef.current = false;
      pushLuma(TIMEOUT_REPLY_TEXT, null, Date.now() - t0Ref.current);
    }, remaining);
  }, [endTurn, pushLuma]);

  useEffect(() => () => { if (turnTimeoutRef.current) clearTimeout(turnTimeoutRef.current); }, []);

  const handleNarration = useCallback((text) => {
    if (!text) return;
    setMessages((prev) => [...prev, { role: 'assistant', text, _live: true, at: new Date().toISOString() }]);
    wsTurnHasNarrationRef.current = true;
    setIsTyping(true);
    // A chunk arriving is proof the channel is alive; push the silence
    // deadline back out (never past the hard ceiling armTurnTimeout itself
    // enforces) instead of cutting off a reply that is genuinely in progress.
    if (turnActiveRef.current) armTurnTimeout();
  }, [armTurnTimeout]);

  // ⬡B:ccwa_core.use_mood_session:FIX:chatter_dedup_reply_priority:20260615⬡
  // handleComplete: (1) prioritise frame.reply (WS sends reply not text at top level),
  // (2) clear _live chatter narration before adding the final message so the thread
  // never shows both the streaming preview AND the final reply.
  const handleComplete = useCallback((frame) => {
    // The watchdog may already have closed this turn honestly (silence past
    // the cap) before a late frame arrives. Once closed, stay closed -- do
    // not append a second, contradicting reply behind the one the person
    // already read.
    if (!turnActiveRef.current) return;
    endTurn();
    const replyText  = frame.reply ?? frame.text ?? '';
    const routeField = frame.route ?? null;
    const durationMs = frame.duration_ms ?? (Date.now() - t0Ref.current);
    if (replyText) setMessages((prev) => [
      ...prev.filter((m) => !m._live),
      { role: 'assistant', text: replyText, at: new Date().toISOString() }
    ]);
    setIsTyping(false);
    wsTurnHasNarrationRef.current = false;
    pushLuma(replyText, routeField, durationMs);
  }, [pushLuma, endTurn]);

  const handleWsError = useCallback((reason) => { if (reason === 'ws_max_retries') setIsTyping(false); }, []);

  const { wsReady, sendMessage } = useAirSocket({
    onNarration: handleNarration, onComplete: handleComplete, onError: handleWsError,
    onStatusChange: () => {}, channel, hamHint,
  });

  const sendViaHttp = useCallback(async (text) => {
    const t0 = Date.now();
    let replyText = ''; let routeField = null; let durationMs = null;
    // ⬡B:DC499D0C:FIX:http_fallback_had_no_ceiling:20260801⬡
    // Plain fetch has no default timeout; a backend that accepts the
    // connection and never answers hung this promise, and isTyping with it,
    // for as long as the browser itself was willing to wait (which is to say,
    // in practice, forever). AbortController gives it the same real ceiling
    // as the watchdog below, so a stalled backend produces the honest line
    // this catch block already writes for a rejected fetch, on a bound a
    // person can actually sit through.
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const killer = ac ? setTimeout(() => ac.abort(), HTTP_FETCH_TIMEOUT_MS) : null;
    try {
      const res = await fetch(AIR_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text, channel, hamHint }), signal: ac?.signal });
      try {
        const json = await res.json();
        replyText  = json.reply ?? json.text ?? JSON.stringify(json);
        routeField = json.route ?? null;
        durationMs = json.duration_ms ?? (Date.now() - t0);
      } catch (_) { replyText = await res.clone().text(); durationMs = Date.now() - t0; }
    } catch (err) { replyText = TIMEOUT_REPLY_TEXT; durationMs = Date.now() - t0; }
    finally { if (killer) clearTimeout(killer); }
    // Same late-arrival guard as handleComplete: if the silence watchdog
    // already closed this turn honestly, an HTTP reply that shows up after
    // does not get to contradict it with a second message.
    if (!turnActiveRef.current) return;
    endTurn();
    setIsTyping(false);
    setMessages((prev) => [...prev, { role: 'assistant', text: replyText, at: new Date().toISOString() }]);
    pushLuma(replyText, routeField, durationMs);
  }, [channel, hamHint, pushLuma, endTurn]);

  const onSend = useCallback((text) => {
    if (!text?.trim()) return;
    setMessages((prev) => [...prev, { role: 'user', text, at: new Date().toISOString() }]);
    setIsTyping(true);
    wsTurnHasNarrationRef.current = false;
    t0Ref.current = Date.now();
    turnActiveRef.current = true;
    armTurnTimeout();
    const sent = sendMessage(text);
    if (!sent) sendViaHttp(text);
  }, [sendMessage, sendViaHttp, armTurnTimeout]);

  const voiceClient = useMemo(() => ({ hamHint }), [hamHint]);
  const handleTranscript = useCallback((text) => { if (text?.trim()) onSend(text.trim()); }, [onSend]);
  const voice = useVaraVoice({ client: voiceClient, onTranscript: handleTranscript, channel });
  const auraState = toAuraState(voice.state, voice.isMuted);
  const amplitude = voice.liveCaption?.level ?? undefined;
  const handleAuraTap = useCallback(() => { const active = voice.state && voice.state !== 'idle'; if (active) voice.stop(); else voice.start(); }, [voice]);

  const handleAgentAsk = useCallback(async (agentName, text) => {
    const userMsg = { role: 'user', text, ts: Date.now() };
    setThreads((prev) => ({ ...prev, [agentName]: [...(prev[agentName] ?? []), userMsg] }));
    setWorking((prev) => ({ ...prev, [agentName]: true }));
    // Same shape as sendViaHttp above and the same fix: an unbounded fetch
    // left this agent's "working" indicator able to spin forever against a
    // backend that never answers.
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const killer = ac ? setTimeout(() => ac.abort(), HTTP_FETCH_TIMEOUT_MS) : null;
    try {
      const res = await fetch(AIR_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text, channel, agent: agentName, hamHint }), signal: ac?.signal });
      let replyText = '';
      try { const json = await res.json(); replyText = json.reply ?? json.text ?? JSON.stringify(json); } catch (_) { replyText = await res.text(); }
      const agentMsg = { role: 'agent', text: replyText, ts: Date.now() };
      setThreads((prev) => ({ ...prev, [agentName]: [...(prev[agentName] ?? []), agentMsg] }));
    } catch (err) {
      const agentMsg = { role: 'agent', text: TIMEOUT_REPLY_TEXT, ts: Date.now() };
      setThreads((prev) => ({ ...prev, [agentName]: [...(prev[agentName] ?? []), agentMsg] }));
    } finally { if (killer) clearTimeout(killer); setWorking((prev) => ({ ...prev, [agentName]: false })); }
  }, [channel, hamHint]);

  return {
    messages, setMessages, isTyping, onSend, wsReady,
    voice, auraState, amplitude, handleAuraTap,
    lumaEntries, handleAgentAsk, threads, working,
  };
}

export default useMoodSession;
